import { type FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
// Google sign-in is hidden for now; uncomment these and the marked code below to bring it back
// import Google from '@/assets/store/icon/google-icon.svg';
import Logo from '@/assets/store/icon/logo.svg';
// import GoogleSignInButton from '@/domains/auth/components/GoogleSignInButton/GoogleSignInButton';
import useAuth from '@/domains/auth/hooks/useAuth';
import { FORGOT_PASSWORD_ROUTE, SIGNUP_ROUTE } from '@/domains/auth/utils/authRoutes';
import InputMobile from '@/domains/mobile-web/components/InputMobile/InputMobile';
import PasswordMobile from '@/domains/mobile-web/components/PasswordMobile/PasswordMobile';
import TopBarHeader from '@/domains/mobile-web/components/TopBarHeader/TopBarHeader';
import WrapperMobile from '@/shared/components/layout/WrapperMobile/WrapperMobile';
import Button from '@/shared/components/ui/Button';
import HeaderMobile from '@/shared/components/ui/HeaderMobile/HeaderMobile';
import Seo from '@/shared/components/ui/Seo/Seo';
import { DEFAULT_LANDING_ROUTE, redirectTarget } from '@/shared/utils/loginRedirect';

// import { showToast } from '@/shared/utils/showToast';

// const GOOGLE_ERROR_MESSAGE = 'Gagal masuk dengan Google. Silakan coba lagi.';
const LOGIN_ERROR_MESSAGE = 'Gagal masuk. Silakan coba lagi.';

const LogIn = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // The screen that sent the user here (home when there is none)
  const returnTo = redirectTarget(searchParams);

  // Google sign-in hidden: add handleGoogleLogin back here when re-enabling it
  const { handlePasswordLogin, getIsLogin } = useAuth({
    onLoginFail: (err) => {
      // request.ts puts the backend's message on err.message
      const message = err instanceof Error && err.message ? err.message : LOGIN_ERROR_MESSAGE;
      setFormError(message);
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: only checks the persisted session on mount
  useEffect(() => {
    if (getIsLogin()) {
      navigate(returnTo, { replace: true });
    }
  }, []);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!identifier.trim() || !password || isSubmitting) return;
    setIsSubmitting(true);
    setFormError('');
    try {
      await handlePasswordLogin({ identifier: identifier.trim(), password }, returnTo);
    } finally {
      setIsSubmitting(false);
    }
  };

  // const onGoogleCredential = async (credential: string) => {
  //   setIsSubmitting(true);
  //   setFormError('');
  //   try {
  //     await handleGoogleLogin(credential, returnTo);
  //   } finally {
  //     setIsSubmitting(false);
  //   }
  // };

  // Back to the previous screen; opened directly (no history in this app) = home
  const onBack = () => {
    const historyIndex = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (historyIndex > 0) {
      navigate(-1);
    } else {
      navigate(DEFAULT_LANDING_ROUTE, { replace: true });
    }
  };

  return (
    <main className="h-screen w-full overflow-hidden justify-between pb-[50px] bg-[linear-gradient(180deg,#FFF_0%,#007AFF_250.56%)]">
      <Seo
        title="Masuk"
        description="Masuk ke akun Rumah Kebijaksanaan untuk berbelanja buku, berdonasi, dan mengikuti program afiliasi."
        path="/login"
      />
      <HeaderMobile className="bg-transparent!">
        <TopBarHeader type="back" title="Masuk" onBack={onBack} />
      </HeaderMobile>

      <WrapperMobile className="flex-1 overflow-auto m-auto h-full">
        <form
          onSubmit={onSubmit}
          noValidate
          className="shadowCard h-fit p-6 flex-col flex gap-6 rounded-[24px] mt-[90px] sm:m-auto md:m-auto lg:m-auto"
        >
          <figure>
            <img
              src={Logo}
              alt="logo rumah kebijaksanaan"
              loading="eager"
              className="w-auto h-auto"
              decoding="async"
            />
          </figure>

          <div className="w-full">
            <p className="title2medium text-black">Masuk Ke Akun Anda</p>
            <p className="reg12 text-text-grey">
              Masukkan nama pengguna dan kata sandi Anda untuk melanjutkan.
            </p>
          </div>

          <div className="w-full flex flex-col gap-3">
            <InputMobile
              label="Username/Email"
              name="username"
              placeholder="Masukkan username atau email"
              autoComplete="username"
              value={identifier}
              onChange={(event) => setIdentifier(event.target.value)}
            />
            <PasswordMobile
              label="Password"
              name="password"
              placeholder="Masukkan password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              error={formError}
              labelAction={
                <Link to={FORGOT_PASSWORD_ROUTE} className="reg12 text-text-blue-100">
                  Lupa Password?
                </Link>
              }
            />
          </div>

          <div className="flex flex-col gap-4">
            <Button
              type="submit"
              customButton="btn-fill-blue"
              disabled={!identifier.trim() || !password || isSubmitting}
            >
              <span className="title2medium text-white">
                {isSubmitting ? 'Memproses...' : 'Masuk'}
              </span>
            </Button>

            {/* Google sign-in hidden for now
            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-blue-cs-200" />
              <span className="reg12 text-text-grey">atau</span>
              <div className="h-px flex-1 bg-blue-cs-200" />
            </div>

            <GoogleSignInButton
              className="w-full"
              disabled={isSubmitting}
              onCredential={onGoogleCredential}
              onError={() =>
                showToast({ type: 'error', title: 'Gagal masuk', message: GOOGLE_ERROR_MESSAGE })
              }
            >
              (Visual only: the transparent Google button on top receives clicks and focus)
              <Button
                type="button"
                customButton="btn-fill-white"
                tabIndex={-1}
                aria-hidden
                disabled={isSubmitting}
                className="w-full group-hover:opacity-70 group-focus-within:ring-2 group-focus-within:ring-blue-cs-200"
              >
                <img src={Google} alt="google" />
                <span className="body1medium text-black">Lanjutkan dengan Google</span>
              </Button>
            </GoogleSignInButton>
            */}
          </div>

          <p className="reg12 text-text-grey text-center">
            Belum punya akun?{' '}
            <Link to={SIGNUP_ROUTE} className="text-text-blue-100">
              Daftar Sekarang
            </Link>
          </p>
          <p className="text-center reg12 text-text-grey max-w-[400px] gap-1 inline-flex flex-wrap items-center justify-center">
            Dengan melanjutkan, kamu menyetujui{' '}
            <Link to={`/store/tnc`} className="text-text-blue-100">
              Syarat & Ketentuan
            </Link>{' '}
            serta
            <Link to={`/store/privacy-police`} className="text-text-blue-100">
              Kebijakan Privasi
            </Link>
            RUMAH KEBIJAKSANAAN
          </p>
        </form>
      </WrapperMobile>
    </main>
  );
};

export default LogIn;
